import type { Question } from '@l2u/ui/lib/types.ts';

// Given the questions loaded for ONE questionnaire, return the questions that
// removing `categoryId` will delete: the questions directly in that category,
// plus any that cascade away because they hang off a deleted question —
// follow-ups (parent_id) and per-option mirrors (instance_of), transitively.
//
// Mirrors the DB behavior: the API deletes the (questionnaire, category)
// questions and the FKs questions.parent_id / questions.instance_of (both
// ON DELETE CASCADE) remove everything hanging off them, even in other
// categories. Pure; drives the removal-confirmation copy. The caller is
// responsible for passing only this questionnaire's questions (the builder's
// query is already scoped by questionnaire_id), so questions belonging to
// other questionnaires that share the global category are never considered.
export function questionsDeletedByRemoving(
  questions: Question[],
  categoryId: string,
): Question[] {
  // Soft-deleted (retired) questions are ignored here: the server-side unlink
  // guard counts only active questions, so the removal warning must match.
  const active = questions.filter((q) => !q.deleted_at);
  const doomed = new Set<string>();
  for (const q of active) {
    if (q.category_id === categoryId) doomed.add(q.id);
  }

  // Grow the set until it stabilises: a question dies if its parent or its
  // authored original is already doomed.
  let grew = true;
  while (grew) {
    grew = false;
    for (const q of active) {
      if (doomed.has(q.id)) continue;
      const hangsOffDoomed =
        (q.parent_id !== null && doomed.has(q.parent_id)) ||
        (q.instance_of !== null && doomed.has(q.instance_of));
      if (hangsOffDoomed) {
        doomed.add(q.id);
        grew = true;
      }
    }
  }

  return active.filter((q) => doomed.has(q.id));
}
